<template>
  <div>
    <div :class="{'modal': true, 'is-active': !showResult}">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="box" style="max-width: 480px; margin: auto;">

          <div class="field partner-head">
            <img v-if="partner.logo" :src="partner.logo" class="partner-logo" />
            <div>
              <p class="title is-5" style="margin-bottom: 0.2rem;">Enganche {{ partner.name }}</p>
              <p class="is-size-7 has-text-grey">
                {{ isAmountMode ? 'Monto del contrato que se descuenta del valor del equipo.' : 'Porcentaje del valor del equipo que se descuenta para cubrir el enganche.' }}
              </p>
            </div>
          </div>

          <div v-if="submittedAmount !== null" class="submitted">
            <div class="breakdown-row">
              <span>Cargado por el usuario</span>
              <strong>${{ formatMoney(submittedAmount) }}</strong>
            </div>
            <div class="breakdown-row">
              <span class="is-size-7 has-text-grey">El {{ formatDate(deduction.submittedAt) }}</span>
              <span class="tag is-rounded" :class="submittedTag.class">{{ submittedTag.label }}</span>
            </div>
            <button
              v-if="isInReview"
              class="button is-success is-fullwidth is-rounded"
              style="margin-top: 0.6rem;"
              :class="{ 'is-loading': loadingStatus }"
              :disabled="loadingStatus"
              @click="accept"
            >
              Aceptar ${{ formatMoney(submittedAmount) }}
            </button>
          </div>

          <p v-else-if="deduction.skipped" class="help is-warning-text field">
            El usuario eligió "No lo sé, que GivU lo calcule".
          </p>

          <p v-else-if="deduction.hasDeduction" class="help field">
            El enganche actual (${{ deduction.coveredAmountLabel }}) lo cargó GivU, no el usuario.
          </p>

          <div class="tabs is-toggle is-small is-fullwidth">
            <ul>
              <li :class="{ 'is-active': isAmountMode }"><a @click="mode = 'amount'">Monto</a></li>
              <li :class="{ 'is-active': !isAmountMode }"><a @click="mode = 'percentage'">Porcentaje</a></li>
            </ul>
          </div>

          <div v-if="isAmountMode" class="field">
            <label class="label">{{ isInReview ? 'Corregir monto de enganche' : 'Monto de enganche' }}</label>
            <div class="control has-icons-left">
              <input
                class="input is-medium"
                type="number"
                min="0"
                step="1"
                v-model="amount"
                placeholder="Ej: 2500"
              />
              <span class="icon is-medium is-left">$</span>
            </div>
          </div>

          <div v-else class="field">
            <label class="label">Porcentaje de enganche</label>
            <div class="control has-icons-right">
              <input
                class="input is-medium"
                type="number"
                min="0"
                max="100"
                step="0.01"
                v-model="percentage"
                placeholder="Ej: 30"
              />
              <span class="icon is-medium is-right">%</span>
            </div>
          </div>

          <div class="breakdown">
            <p class="breakdown-title">Así lo ve el usuario en la app</p>
            <div class="breakdown-row">
              <span>Valor del equipo</span>
              <strong>${{ formatMoney(deviceValue) }}</strong>
            </div>
            <div class="breakdown-row">
              <span>Enganche cubierto{{ coveredPercentage !== null ? ` (${coveredPercentage}%)` : '' }}</span>
              <strong v-if="hasValue" class="has-text-danger">- ${{ formatMoney(coveredAmount) }}</strong>
              <strong v-else class="has-text-grey">Pendiente</strong>
            </div>
            <div class="breakdown-row is-total">
              <span>Saldo a acreditar</span>
              <strong v-if="hasValue">${{ formatMoney(creditedAmount) }}</strong>
              <strong v-else class="has-text-grey">Pendiente</strong>
            </div>
          </div>

          <p v-if="deviceValue <= 0" class="help is-warning-text">
            Este producto todavía no tiene oferta ni precio de mercado cargado, por eso el valor del equipo es $0.
          </p>
          <p v-if="isInReview" class="help">
            Al guardar queda validado y se le avisa al usuario{{ isAmountMode ? ' si hubo ajuste' : '' }}.
          </p>

          <div class="columns" style="margin-top: 0.5rem;">
            <div class="column">
              <button class="button is-danger is-outlined is-fullwidth is-rounded is-medium" @click="close">Cerrar</button>
            </div>
            <div class="column">
              <button
                class="button is-danger is-fullwidth is-rounded is-medium"
                :class="{ 'is-loading': loadingStatus }"
                :disabled="loadingStatus"
                @click="submit"
              >
                Guardar
              </button>
            </div>
          </div>

          <div class="has-text-centered" v-if="deduction.hasDeduction">
            <a class="is-size-7 has-text-grey" @click="clear">Quitar el enganche cargado</a>
          </div>

          <div v-if="error" class="notification is-danger" style="margin-top: 1rem;">
            <p>{{ error }}</p>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="close"></button>
    </div>

    <div :class="{'modal': true, 'is-active': showResult}">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="box" style="max-width: 350px; margin: auto;">
          <div class="field">
            <div class="control">
              <p>{{ apiResult }}</p>
            </div>
          </div>
          <div class="field is-grouped is-grouped-right">
            <div class="control">
              <button class="button is-danger is-outlined is-rounded is-medium" @click="close">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="close"></button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

const emptyIfNull = (value) => value === null || value === undefined ? '' : value

export default {
  props: {
    partner: {
      type: Object,
      required: true
    },
    close: {
      type: Function,
      default: () => {}
    },
    action: {
      type: Function,
      default: () => {}
    },
    validateAction: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    const deduction = this.partner.deduction || {}
    const usesAmount = deduction.source === 'amount' ||
      (deduction.source !== 'percentage' && (!!deduction.minAmount || emptyIfNull(deduction.submittedAmount) !== ''))
    return {
      mode: usesAmount ? 'amount' : 'percentage',
      percentage: deduction.source === 'percentage' ? emptyIfNull(deduction.percentage) : '',
      amount: deduction.source === 'amount' ? deduction.coveredAmount : emptyIfNull(deduction.submittedAmount),
      deviceValue: deduction.deviceValue || 0,
      loadingStatus: false,
      apiResult: null,
      error: null,
      showResult: false
    }
  },
  computed: {
    deduction () {
      return this.partner.deduction || {}
    },
    isAmountMode () {
      return this.mode === 'amount'
    },
    submittedAmount () {
      return emptyIfNull(this.deduction.submittedAmount) === '' ? null : this.deduction.submittedAmount
    },
    isInReview () {
      return this.deduction.status === 'in_review'
    },
    submittedTag () {
      if (this.isInReview) return { label: 'Sin validar', class: 'is-warning' }
      if (this.deduction.adjusted) return { label: `GivU lo corrigió a $${this.deduction.validatedAmountLabel}`, class: 'is-info' }
      return { label: 'Validado por GivU', class: 'is-success' }
    },
    normalizedPercentage () {
      return Number(this.percentage)
    },
    normalizedAmount () {
      return Math.round(Number(this.amount))
    },
    hasPercentage () {
      return this.percentage !== '' && this.percentage !== null &&
        Number.isFinite(this.normalizedPercentage) &&
        this.normalizedPercentage >= 0 && this.normalizedPercentage <= 100
    },
    hasAmount () {
      return this.amount !== '' && this.amount !== null &&
        Number.isFinite(this.normalizedAmount) && this.normalizedAmount >= 0
    },
    hasValue () {
      return this.isAmountMode ? this.hasAmount : this.hasPercentage
    },
    coveredAmount () {
      if (!this.hasValue) return 0
      return this.isAmountMode
        ? this.normalizedAmount
        : Math.round(this.deviceValue * (this.normalizedPercentage / 100))
    },
    coveredPercentage () {
      if (!this.hasValue) return null
      if (!this.isAmountMode) return this.normalizedPercentage
      return this.deviceValue > 0 ? Math.round((this.normalizedAmount / this.deviceValue) * 10000) / 100 : null
    },
    creditedAmount () {
      return Math.max(this.deviceValue - this.coveredAmount, 0)
    }
  },
  methods: {
    formatMoney (amount) {
      return Number(amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    formatDate (date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : ''
    },
    async submit () {
      if (!this.isAmountMode) {
        if (!this.hasPercentage) {
          this.error = 'Ingresá un porcentaje entre 0 y 100'
          return
        }
        await this.save(() => this.action(this.normalizedPercentage))
        return
      }
      if (!this.hasAmount) {
        this.error = 'Ingresá un monto válido'
        return
      }
      if (this.deviceValue > 0 && this.normalizedAmount > this.deviceValue) {
        this.error = 'El enganche no puede superar el valor del equipo'
        return
      }
      await this.save(() => this.validateAction(this.normalizedAmount))
    },
    async accept () {
      await this.save(() => this.validateAction(this.submittedAmount))
    },
    async clear () {
      await this.save(() => this.action(''))
    },
    async save (request) {
      try {
        this.loadingStatus = true
        const payload = await request()

        if (!payload) {
          this.error = 'No se pudo guardar el enganche. Intentá de nuevo.'
          return
        }

        this.error = null
        this.apiResult = payload.hasDeduction
          ? `Enganche de $${payload.coveredAmountLabel}${payload.percentage !== null ? ` (${payload.percentage}%)` : ''} guardado. Saldo a acreditar: $${payload.creditedAmountLabel}`
          : 'Se quitó el enganche de este producto.'
        this.showResult = true
      } catch (err) {
        this.error = 'Error al realizar la operación. Por favor, inténtalo de nuevo.'
        this.apiResult = null
      } finally {
        this.loadingStatus = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.box {
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
}
.field {
  margin-bottom: 1rem;
}
.button.is-fullwidth {
  width: 100%;
}
.modal-background {
  background-color: rgba(0, 0, 0, 0.5);
}
.is-danger {
  background-color: #EB5965;
}
.partner-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.partner-logo {
  max-height: 38px;
  max-width: 90px;
  object-fit: contain;
}
.breakdown {
  background-color: #fafafa;
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 1rem;
}
.breakdown-title {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9a9a9a;
  margin-bottom: 0.6rem;
}
.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
}
.breakdown-row.is-total {
  border-top: 1px solid #e4e4e4;
  margin-top: 0.35rem;
  padding-top: 0.6rem;
  font-size: 1.05rem;
}
.submitted {
  border: 1px solid #f0d58a;
  background-color: #fffaeb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}
.is-warning-text {
  color: #946c00;
}
a {
  cursor: pointer;
}
</style>
